import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { 
  getUserMovieInteraction,
  addToWatchlist,
  removeFromWatchlist,
  markAsWatched,
  unmarkAsWatched
} from '../../services/movieInteractionApi';

const WatchlistTest = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [interaction, setInteraction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);

  // Test movie data
  const testMovie = {
    id: '507f1f77bcf86cd799439011', // MongoDB ObjectId format
    name: 'Test Movie',
    title: 'Test Movie'
  };

  const addResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, timestamp: new Date() }]);
  };

  const loadInteraction = async () => {
    if (!user?.id) return;
    try {
      const result = await getUserMovieInteraction(user.id, testMovie.id);
      setInteraction(result);
      addResult('Load Interaction', true, `Loaded: ${JSON.stringify(result)}`);
    } catch (error) {
      addResult('Load Interaction', false, error.message);
    }
  };

  const testAddToWatchlist = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const result = await addToWatchlist(user.id, testMovie.id);
      setInteraction(result);
      addResult('Add to Watchlist', true, 'Successfully added to watchlist');
      addNotification({ type: 'success', message: 'Added to watchlist' });
    } catch (error) {
      addResult('Add to Watchlist', false, error.message);
      addNotification({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testRemoveFromWatchlist = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const result = await removeFromWatchlist(user.id, testMovie.id);
      setInteraction(result);
      addResult('Remove from Watchlist', true, 'Successfully removed from watchlist');
      addNotification({ type: 'success', message: 'Removed from watchlist' });
    } catch (error) {
      addResult('Remove from Watchlist', false, error.message);
      addNotification({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testMarkAsWatched = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const result = await markAsWatched(user.id, testMovie.id);
      setInteraction(result);
      addResult('Mark as Watched', true, 'Successfully marked as watched');
      addNotification({ type: 'success', message: 'Marked as watched' });
    } catch (error) {
      addResult('Mark as Watched', false, error.message);
      addNotification({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testUnmarkAsWatched = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const result = await unmarkAsWatched(user.id, testMovie.id);
      setInteraction(result);
      addResult('Unmark as Watched', true, 'Successfully unmarked as watched');
      addNotification({ type: 'success', message: 'Unmarked as watched' });
    } catch (error) {
      addResult('Unmark as Watched', false, error.message);
      addNotification({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  useEffect(() => {
    if (user?.id) {
      loadInteraction();
    }
  }, [user?.id]);

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to test watchlist functionality</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Watchlist & Watched List Test
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current State
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            User ID: {user.id}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Test Movie ID: {testMovie.id}
          </Typography>
          
          {interaction ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Current Interaction:</Typography>
              <Typography variant="body2">
                • In Watchlist: {interaction.inWatchlist ? '✅ Yes' : '❌ No'}
              </Typography>
              <Typography variant="body2">
                • Watched: {interaction.watched ? '✅ Yes' : '❌ No'}
              </Typography>
              <Typography variant="body2">
                • Favorite: {interaction.isFavorite ? '✅ Yes' : '❌ No'}
              </Typography>
              <Typography variant="body2">
                • Not Interested: {interaction.notInterested ? '✅ Yes' : '❌ No'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No interaction found
            </Typography>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button 
          variant="outlined" 
          onClick={loadInteraction}
          disabled={loading}
        >
          Refresh State
        </Button>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={testAddToWatchlist}
          disabled={loading || interaction?.inWatchlist}
        >
          Add to Watchlist
        </Button>
        
        <Button 
          variant="contained" 
          color="warning"
          onClick={testRemoveFromWatchlist}
          disabled={loading || !interaction?.inWatchlist}
        >
          Remove from Watchlist
        </Button>
        
        <Button 
          variant="contained" 
          color="success"
          onClick={testMarkAsWatched}
          disabled={loading || interaction?.watched}
        >
          Mark as Watched
        </Button>
        
        <Button 
          variant="contained" 
          color="error"
          onClick={testUnmarkAsWatched}
          disabled={loading || !interaction?.watched}
        >
          Unmark as Watched
        </Button>
        
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={clearResults}
        >
          Clear Results
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          
          {testResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No tests run yet
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {testResults.map((result, index) => (
                <Alert 
                  key={index}
                  severity={result.success ? 'success' : 'error'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    {result.test} - {result.timestamp.toLocaleTimeString()}
                  </Typography>
                  <Typography variant="body2">
                    {result.message}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WatchlistTest;